import { computed, Injectable, signal } from '@angular/core';
import { IMaintenancePopup } from '@domain/auth';

export type MaintenancePopupSource = 'backend' | 'fallback';

export type MaintenancePopupVm = {
  source: MaintenancePopupSource;
  blocking: boolean;
  title: string;
  message: string;
  retryAfterSeconds: number | null;
  until: string | null;
  code: string | null;
};

const FALLBACK_POPUP: MaintenancePopupVm = {
  source: 'fallback',
  blocking: true,
  title: 'Проводим технические работы',
  message: 'Сервис временно недоступен, готовим обновление для вас.',
  retryAfterSeconds: null,
  until: null,
  code: 'backend_unavailable',
};

@Injectable({
  providedIn: 'root',
})
export class MaintenanceService {
  readonly #popup = signal<MaintenancePopupVm | null>(null);
  readonly #retrying = signal(false);

  readonly popup = this.#popup.asReadonly();
  readonly retrying = this.#retrying.asReadonly();
  readonly isVisible = computed(() => this.#popup() !== null);

  syncWithAuthPopup(config?: IMaintenancePopup | null): void {
    if (config?.show === true) {
      this.#popup.set({
        source: 'backend',
        blocking: config.blocking !== false,
        title: config.title?.trim() || FALLBACK_POPUP.title,
        message: config.message?.trim() || FALLBACK_POPUP.message,
        retryAfterSeconds: typeof config.retry_after_seconds === 'number' ? config.retry_after_seconds : null,
        until: config.until ?? null,
        code: config.code ?? null,
      });
      return;
    }

    this.hide();
  }

  showFallbackPopup(): void {
    const current = this.#popup();
    if (current?.source === 'backend') {
      return;
    }

    this.#popup.set(FALLBACK_POPUP);
  }

  hide(): void {
    this.#popup.set(null);
    this.#retrying.set(false);
  }

  startRetry(): void {
    this.#retrying.set(true);
  }

  finishRetry(): void {
    this.#retrying.set(false);
  }

  isBackendUnavailableError(error: unknown): boolean {
    const status = this.#readStatus(error);
    if (status === 0 || status === 502 || status === 503 || status === 504) {
      return true;
    }

    const code = this.#readCode(error);
    return code === 'PG_AUTH_TIMEOUT' || code === 'TIMEOUT';
  }

  #readStatus(error: unknown): number | null {
    const candidate = this.#asObject(error)?.['status'];
    return typeof candidate === 'number' ? candidate : null;
  }

  #readCode(error: unknown): string | null {
    const root = this.#asObject(error);
    const code = root?.['code'];
    if (typeof code === 'string') {
      return code;
    }

    const name = root?.['name'];
    return typeof name === 'string' ? name : null;
  }

  #asObject(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' ? value as Record<string, unknown> : null;
  }
}
